import React, { useState, useEffect, useCallback } from 'react'; 
import { Container, Row, Col, Button, Table, Alert, Spinner, Modal, Form, Card } from 'react-bootstrap';
import AddDealerModal from '../components/AddDealerModal';
import EditDealerModal from '../components/EditDealerModal';
import 'bootstrap/dist/css/bootstrap.min.css';
import { getAuthHeaders } from '../utils/authHelpers';

const API_ROOT_URL = process.env.REACT_APP_API_ROOT_URL;
const DEALERS_API_URL = `${API_ROOT_URL}/dealers`;

function DealersPage() {
  const [allDealers, setAllDealers] = useState([]); // Stores all fetched dealers
  const [displayedDealers, setDisplayedDealers] = useState([]); // Stores filtered dealers
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [dealerToEdit, setDealerToEdit] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [dealerToDelete, setDealerToDelete] = useState(null);
  const [pageMessage, setPageMessage] = useState('');
  const [pageMessageType, setPageMessageType] = useState('');
  const [managerPassword, setManagerPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Search state (status filter removed)
  const [searchTerm, setSearchTerm] = useState('');

  const sortDealersByName = (dealers) => {
    return [...dealers].sort((a, b) =>
      (a.dealerName || '').localeCompare(b.dealerName || '', undefined, { sensitivity: 'base' })
    );
  };

  const fetchDealers = useCallback(async () => {
    setLoading(true);
    setError('');
    setPageMessage('');
    setPageMessageType('');

    try {
      const token = localStorage.getItem('authToken'); 

      const response = await fetch(DEALERS_API_URL, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setAllDealers(sortDealersByName(data));
    } catch (err) {
      console.error('Error fetching dealers:', err);
      setError('Failed to load dealers. Please ensure you are logged in and have permission to view this data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDealers();
  }, [fetchDealers]);

  // Effect to apply filters whenever allDealers or searchTerm changes
  useEffect(() => {
    const applyFilters = () => {
      let filtered = allDealers;

      // Apply search term filter
      if (searchTerm) {
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        filtered = filtered.filter(dealer =>
          dealer.dealerId.toLowerCase().includes(lowerCaseSearchTerm) ||
          dealer.dealerName.toLowerCase().includes(lowerCaseSearchTerm) ||
          (dealer.contactPerson && dealer.contactPerson.toLowerCase().includes(lowerCaseSearchTerm)) ||
          (dealer.contactEmail && dealer.contactEmail.toLowerCase().includes(lowerCaseSearchTerm)) ||
          (dealer.contactPhone && dealer.contactPhone.toLowerCase().includes(lowerCaseSearchTerm)) ||
          (dealer.address && Object.values(dealer.address).some(val => 
            typeof val === 'string' && val.toLowerCase().includes(lowerCaseSearchTerm)
          ))
        );
      }

      setDisplayedDealers(sortDealersByName(filtered));
    };

    applyFilters();
  }, [allDealers, searchTerm]); // Status filter dependency removed

  const handlePageMessage = (message, type) => {
    setPageMessage(message);
    setPageMessageType(type);
  };

  const handleShowAddModal = () => {
    setPageMessage('');
    setPageMessageType('');
    setShowAddModal(true);
  }
  const handleCloseAddModal = () => setShowAddModal(false);

  const handleShowEditModal = (dealer) => {
    setPageMessage('');
    setPageMessageType('');
    setDealerToEdit(dealer);
    setShowEditModal(true);
  };
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setDealerToEdit(null);
  };

  const handleDeleteClick = (dealer) => {
    setPageMessage('');
    setPageMessageType('');
    setDealerToDelete(dealer);
    setManagerPassword('');
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!dealerToDelete) return;
    if (!managerPassword) {
      handlePageMessage('Manager password is required to delete a dealer.', 'warning');
      return;
    }
    setPageMessage('');
    setPageMessageType('');
    setLoading(true); // Use global loading for delete action
    try {
      const response = await fetch(`${DEALERS_API_URL}/${dealerToDelete.dealerId}`, {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ managerSecret: managerPassword }),
      });
      const result = await response.json();
      if (response.ok) {
        fetchDealers(); 
        handlePageMessage(result.message || 'Dealer deleted successfully!', 'success'); // Show success message
      } else {
        handlePageMessage(result.message || 'Failed to delete dealer.', 'danger');
      }
    } catch (error) {
      console.error('Error deleting dealer:', error);
      handlePageMessage('Network error or server is unreachable.', 'danger');
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
      setDealerToDelete(null);
    }
  };

  const handleCloseDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setDealerToDelete(null);
    setManagerPassword('');
    setShowPassword(false);
  };

  const formatAddress = (address) => {
    if (!address) return 'N/A';
    const parts = [];
    if (address.street) parts.push(address.street);
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.zip) parts.push(address.zip);
    if (address.country) parts.push(address.country);
    return parts.length > 0 ? parts.join(', ') : 'N/A';
  };

  if (loading && allDealers.length === 0) { // Check allDealers for initial loading state
    return (
      <Container className="my-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading dealers...</span>
        </Spinner>
        <p className="mt-2">Loading dealers...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Dealers</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container
    fluid
    style={{
      backgroundColor: '#4595ad29',
      minHeight: '100vh',
      paddingTop: '2rem',
      paddingBottom: '2rem',
      margin: 0
    }}
  >

  
    <Container className="my-5">
      <Row className="mb-4 align-items-center">
        <Col>
          <h2 className="mb-0">Dealers Management</h2>
        </Col>
        <Col xs="auto">
          <Button variant="success" onClick={handleShowAddModal}>
            + Add New Dealer
          </Button>
        </Col>
      </Row>
      {pageMessage && <Alert variant={pageMessageType}>{pageMessage}</Alert>}

      {/* Search Bar (status filter removed) */}
      <Row className="mb-4 justify-content-center">
        <Col md={12}>
          <Card className="shadow-sm p-3">
            <Row className="align-items-center">
              <Col md={6} className="mb-3 mb-md-0">
                <Form.Group controlId="searchDealers">
                  <Form.Label className="visually-hidden">Search Dealers</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Search by Dealer ID, Name, Contact, or Address"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Button
                  variant="outline-secondary"
                  onClick={() => { setSearchTerm(''); }}
                  disabled={loading}
                >
                  Clear Filters
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {loading && allDealers.length > 0 && ( // Show loading spinner if refreshing existing data
          <div className="d-flex justify-content-center my-3">
              <Spinner animation="border" size="sm" role="status">
                  <span className="visually-hidden">Refreshing...</span>
              </Spinner>
          </div>
      )}
      {displayedDealers.length === 0 && !loading ? ( // Check displayedDealers for "no dealers found" message
        <Alert variant="info" className="text-center mt-3">No dealers found matching your criteria. Click "Add New Dealer" to get started!</Alert>
      ) : (
        <div className="table-responsive">
          <Table striped bordered hover responsive className="shadow-sm rounded">
            <thead className="table-dark">
              <tr>
                <th>Dealer ID</th>
                <th>Dealer Name</th>
                <th>Contact Person</th>
                <th>Contact Email</th>
                <th>Contact Phone</th>
                <th>Address</th>
                <th>Shipping Preferences</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedDealers.map((dealer) => ( // Render displayedDealers
                <tr key={dealer.dealerId}>
                  <td>{dealer.dealerId}</td>
                  <td>{dealer.dealerName}</td>
                  <td>{dealer.contactPerson || 'N/A'}</td>
                  <td>{dealer.contactEmail || 'N/A'}</td>
                  <td>{dealer.contactPhone || 'N/A'}</td>
                  <td>{formatAddress(dealer.address)}</td>
                  <td>{dealer.shippingPreferences || 'None'}</td>
                  <td>
                    <Button
                      variant="warning"
                      size="sm"
                      className="me-2"
                      onClick={() => handleShowEditModal(dealer)}
                      disabled={loading}
                    >
                      Edit
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => handleDeleteClick(dealer)} disabled={loading}>Delete</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
      {/* Add Dealer Modal */}
      <AddDealerModal
        show={showAddModal}
        handleClose={handleCloseAddModal}
        refreshDealers={fetchDealers}
        setSubmissionMessage={handlePageMessage}
      />
      {/* Edit Dealer Modal */}
      {dealerToEdit && (
        <EditDealerModal
          show={showEditModal}
          handleClose={handleCloseEditModal}
          refreshDealers={fetchDealers}
          dealerToEdit={dealerToEdit}
          setSubmissionMessage={handlePageMessage}
        />
      )}
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={handleCloseDeleteConfirm} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete dealer <strong>{dealerToDelete?.dealerName} ({dealerToDelete?.dealerId})</strong>? This action cannot be undone.
          <Form.Group className="mt-3" controlId="managerPassword">
            <Form.Label>Manager Password</Form.Label>
            <div className="d-flex align-items-center mt-2">
              <Form.Control
                type={showPassword ? "text" : "password"}
                placeholder="Enter manager password to confirm"
                value={managerPassword}
                onChange={(e) => setManagerPassword(e.target.value)}
                autoFocus
              />
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowPassword(prev => !prev)}
                style={{ marginLeft: '0.5rem' }}
              >
                {showPassword ? 'Hide' : 'Show'}
              </Button>
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteConfirm}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteConfirm} 
            disabled={loading || !managerPassword}
          >
            {loading ? <Spinner as="span" animation="border" size="sm" /> : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
    </Container>
  );
}

export default DealersPage;
